import { useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { Link, Query } from "@models/ISchema";
import { ProgrammingRow } from "@services/types/programming-edit/programmingEditTypes";
import { useProgrammingSessionsCrud } from "@services/collections/programming-edit/programmingEditServices";
import {
  adaptPreviewToChangeset,
  adaptUndoToPreviewInput,
  normalizePreviewIds,
  resolveUndoComponentId,
  resolveUndoStageCode,
} from "@services/collections/programming-edit/programmingEditAdapters";
import { MICROPROGRAMMING_PREVIEW_QUERY } from "@services/collections/programming-edit/graphql/microprogrammingPreview.query";

interface UndoAppliedProgrammingParams {
  scenarioId: string;
  originId: string;
  link: Link;
  sectionIds: string[];
  originalSlots: ProgrammingRow[];
}

export type UndoAppliedProgrammingResult =
  | { status: "success" }
  | {
      status: "error";
      reason:
        | "no-slots"
        | "no-stage-code"
        | "no-course-component"
        | "preview-invalid"
        | "not-committed"
        | "failed"
        | "in-progress";
    };

/** Preview saved sessions, then commit the undo only when replacements exist. */
export const useUndoAppliedProgramming = () => {
  const client = useApolloClient();
  const { submitCrud } = useProgrammingSessionsCrud();
  const [isUndoing, setIsUndoing] = useState(false);
  const inFlight = useRef(false);

  const undoAppliedProgramming = async ({
    scenarioId,
    originId,
    link,
    sectionIds,
    originalSlots,
  }: UndoAppliedProgrammingParams): Promise<UndoAppliedProgrammingResult> => {
    if (inFlight.current) return { status: "error", reason: "in-progress" };
    if (!originalSlots.length) return { status: "error", reason: "no-slots" };

    const structureId = link.programmingStructure?.id;
    const stageCode = resolveUndoStageCode(
      link.programmingStructure,
      link.course?.courseComponents,
      originalSlots,
    );
    const componentId = resolveUndoComponentId(link.course?.courseComponents);
    if (!stageCode || !structureId) return { status: "error", reason: "no-stage-code" };
    if (!componentId) return { status: "error", reason: "no-course-component" };

    inFlight.current = true;
    setIsUndoing(true);
    try {
      const { data } = await client.query<Query>({
        query: MICROPROGRAMMING_PREVIEW_QUERY,
        fetchPolicy: "no-cache",
        variables: {
          scenarioId,
          originId,
          input: adaptUndoToPreviewInput(originalSlots, stageCode, structureId),
        },
      });
      const preview = data?.cube?.microprogrammingPreview;
      if (!preview?.sections?.length || preview.validationErrors?.length) {
        return { status: "error", reason: "preview-invalid" };
      }
      const changeset = adaptPreviewToChangeset(
        normalizePreviewIds(preview.sections),
        link,
        sectionIds,
        null,
        componentId,
      );
      if (!changeset.changesets?.creates?.length) {
        return { status: "error", reason: "preview-invalid" };
      }
      const crud = await submitCrud({
        scenarioId,
        originId,
        input: { ...changeset, dryRun: false, skipValidations: true },
      });
      return crud?.commited ? { status: "success" } : { status: "error", reason: "not-committed" };
    } catch {
      return { status: "error", reason: "failed" };
    } finally {
      inFlight.current = false;
      setIsUndoing(false);
    }
  };

  return { undoAppliedProgramming, isUndoing };
};
